import React, { useRef } from 'react';
import { Caption } from 'koba/components/Typography';

import clsx from 'clsx';
import { FILE_TYPES_ARTICLE } from 'utils/constants';
import Button from 'koba/components/Button';
import Icon from 'koba/components/Icon';
import { useTranslation } from 'react-i18next';
import styles from './AttachmentInput.module.scss';

interface AttachmentInputProps {
  accept: string;
  ariaControls: string;
  buttonLabel: string;
  attachmentCount: number;
  isUploading: boolean;
  multiple?: boolean;
  maxFileSizeInMb: number;
  maxFilesAllowed?: number;
  onClose: () => void;
  onFileSelected: (files: File[]) => void;
  onFileSelectedError: (errorMessage: string) => void;
  errorMessage: string;
}

const AttachmentInput = ({
  accept,
  ariaControls,
  buttonLabel,
  attachmentCount = 0,
  isUploading,
  multiple = false,
  maxFileSizeInMb,
  maxFilesAllowed = 1,
  onClose,
  onFileSelected,
  onFileSelectedError,
  errorMessage,
}: AttachmentInputProps) => {
  const { t } = useTranslation();

  const hasAttachment = attachmentCount > 0;

  const validateFiles = (files: File[]): string | null => {
    const maxFileSizeInBytes = maxFileSizeInMb * 1024 * 1024;
    const fileTypeRegex = accept.replace(/,/g, '|').replace('*', '.*');

    if (files.length + attachmentCount > maxFilesAllowed) {
      return `You can only upload up to ${maxFilesAllowed} files.`;
    }

    for (const file of files) {
      if (file.size > maxFileSizeInBytes) {
        return `File ${file.name} is too large. Maximum file size is ${maxFileSizeInMb}MB.`;
      }

      if (!file.type.match(fileTypeRegex)) {
        return `File ${file.name} (${
          file.type
        }) is not accepted. Must be one of the following: ${accept.replace(
          /,/g,
          ', '
        )}`;
      }
    }

    return null;
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = Array.from(event.target.files || []);

    // clear the input value, so that the same file can be uploaded again
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (files) {
      const error = validateFiles(files);

      if (!error) {
        onFileSelected(files);
      } else {
        onFileSelectedError(error);
      }
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const renderAttachmentBox = () => {
    return (
      <div
        className={clsx(styles['file-input'], {
          [styles['has-error']]: errorMessage,
        })}
      >
        {!hasAttachment && (
          <Button
            appearance="knockout"
            ariaLabel={t('Close file input')}
            className={styles['close-button']}
            data-qa="attachment-file-picker__close-button"
            isDisabled={isUploading}
            onClick={onClose}
          >
            <Icon name="x-dismiss" />
          </Button>
        )}
        <Button
          appearance="default"
          className={styles['file-input__upload-button']}
          data-qa="attachment-file-picker__upload-button"
          isDisabled={isUploading}
          size="small"
          onClick={() => inputRef.current?.click()}
        >
          {isUploading && (
            <span className={styles['file-input__upload-spinner']}>
              {t(
                'communities_components-attachmentInput-uploading',
                'Uploading...'
              )}
            </span>
          )}
          {!isUploading && buttonLabel}
        </Button>
        <Caption
          className={styles['file-input__caption']}
          data-qa="attachment-file-picker__caption"
          role={errorMessage ? 'alert' : undefined}
        >
          {errorMessage || (
            <div>
              {multiple && (
                <span>
                  {t(
                    'components-attachmentInput-maximum_size',
                    'Maximum {{maxFilesAllowed}} files, {{maxFileSizeInMb}}MB each.',
                    {
                      maxFileSizeInMb,
                      maxFilesAllowed,
                    }
                  )}{' '}
                </span>
              )}
              <span>See</span>{' '}
              <span>
                <a
                  href={FILE_TYPES_ARTICLE}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t(
                    'components-attachmentInput-accepted_files',
                    'accepted file types'
                  )}
                </a>
              </span>
            </div>
          )}
        </Caption>
      </div>
    );
  };

  return (
    <>
      {renderAttachmentBox()}
      <input
        accept={accept}
        aria-controls={ariaControls}
        data-qa="attachment-file-picker__input"
        multiple={multiple}
        ref={inputRef}
        style={{ display: 'none' }}
        type="file"
        onChange={handleFileChange}
      />
    </>
  );
};

export default AttachmentInput;
